import { CANVAS_URN_TYPES, canvasUrnIsValid } from "./agenticUiState.ts";
import { isSegmentedUrn, isUrn, safeParseURN } from "./urn.ts";

/**
 * What an in-app markdown link means, decided once for every surface that
 * renders agent markdown: the web thread, the iOS thread, HTML email, and SMS.
 *
 * Each of those used to carry its own table of link schemes, and they knew
 * different subsets — an entity URN routed on web and native but went out as a
 * dead `urn:bb:` anchor in email; a rooted `/threads/7` absolutized for email
 * but was an unopenable URL on iOS. Every scheme was broken somewhere, and a new
 * one meant a fifth place to add it and a fifth place to forget.
 *
 * This module answers only what a link *is* and where it goes. How to present
 * the answer stays with the surface: web's nav boundaries still choose canvas
 * vs sidebar vs page from the path, native still shows a contact in a sheet
 * rather than a route, and email still decides what a button looks like.
 *
 * This dir is symlinked into `web/src/types` and `native/src/types`, which is
 * why the resolver lives here — it is the only place all three packages compile.
 * Its tests live in `api/src/tests/types/`, never beside it: native compiles
 * this directory and has no vitest.
 */

export type ResolvedMessageLink =
  /** A route this app serves. `path` is rooted and origin-free, ready to absolutize. */
  | { kind: "app"; path: string }
  /**
   * A named side effect with no destination — the one thing a plain link cannot
   * express, and so the only thing the `action:` scheme is for. Anything that
   * merely navigates is written as the route it navigates to.
   */
  | { kind: "action"; action: KnownAction }
  /** Somewhere else entirely — safe to hand to a browser or leave in an email. */
  | { kind: "external"; url: string }
  /** An in-page jump, meaningful only on a rendered page. */
  | { kind: "anchor"; hash: string }
  /**
   * Nothing this app can address: an unknown scheme, an action with no registry
   * row, a route missing the id it needs, or a malformed href. Surfaces render
   * the link text and drop the target.
   */
  | { kind: "none" };

const NONE: ResolvedMessageLink = { kind: "none" };

/** Scheme prefix for a named side effect (`action:celebrate`). */
export const ACTION_HREF_PREFIX = "action:";

/**
 * Side effects the agent may invoke by linking them. Each renders its link text
 * as ordinary prose on every surface and does its thing only where it is
 * implemented, so an action is always safe to write and never leaves a dead
 * link behind.
 */
export const KNOWN_ACTIONS = ["celebrate"] as const;

export type KnownAction = (typeof KNOWN_ACTIONS)[number];

const isKnownAction = (value: string): value is KnownAction =>
  (KNOWN_ACTIONS as readonly string[]).includes(value);

/**
 * The markdown link title that asks for a standalone call to action rather than
 * a word in a sentence: `[Show me around](/?nux=true "button")`.
 *
 * A title is ordinary CommonMark, so every renderer parses it and nothing has to
 * learn a private syntax. It carries presentation only — the destination stays a
 * real route, which is what keeps the link a link.
 */
export const BUTTON_LINK_TITLE = "button";

/**
 * How many path segments a route needs after its leading one.
 *
 * Mirrors the `<Route>` table in `web/src/App.tsx`, and is the reason to look
 * here when a link renders as plain text: a route the SPA serves but this table
 * omits resolves to `none` on every surface at once, rather than failing on
 * just the one someone was testing.
 */
const APP_ROUTES: Record<string, "bare" | "id" | "either"> = {
  // The landing calendar. Also the tour's destination, which carries its whole
  // payload in the query string.
  "": "bare",
  threads: "id",
  artifacts: "id",
  contacts: "id",
  sources: "id",
  communications: "id",
  attachments: "id",
  // `/calendar?date=…` moves the mounted calendar; `/calendar/event/:id`
  // addresses one event. The event form needs its id, checked below.
  calendar: "either",
  // `/settings`, `/settings/:tab`, and `/settings/:tab/:subId` are all real.
  settings: "either",
  connectors: "bare",
  people: "bare",
  routines: "either",
  skills: "either",
  // `/tasks` and `/tasks/:id`, which the brief links its "On your plate" items to.
  tasks: "either",
  "start-thread": "bare",
  onboarding: "either",
};

/**
 * The `system:` sentinel a built-in routine's URN carries.
 *
 * It marks a routine the registry owns rather than the ent table, and it belongs
 * to the *run* vocabulary — `routine_runs.routine_ent_id`, job payloads,
 * `threads.routine_ent_id`. The routines API addresses a built-in by its bare
 * registry id (`getSystemRoutineById` matches `r.id === id`), so the sentinel has
 * to come off before the id reaches a URL.
 */
const SYSTEM_ROUTINE_URN_PREFIX = "system:";

/**
 * Entity URN types that address a page directly, and the route that serves it.
 *
 * A routine's page is a settings tab rather than a top-level route, which is
 * also how `canvasContentForPath` reads it back.
 */
const URN_ROUTES: Record<string, (id: string) => string> = {
  contact: (id) => `/contacts/${id}`,
  company: (id) => `/contacts/${id}`,
  person: (id) => `/contacts/${id}`,
  user: (id) => `/contacts/${id}`,
  // No `<Route>` serves this yet, so an attachment link lands on NotFound. Kept
  // anyway because it is what the web link builder always meant to emit, and a
  // 404 is recoverable where the `https://urn:bb:attachment:3` it used to
  // produce was not.
  attachment: (id) => `/attachments/${id}`,
  thread: (id) => `/threads/${id}`,
  source: (id) => `/sources/${id}`,
  // Both forms of routine id land on the same page: a user routine is an ent
  // UUID, a built-in is `system:<slug>` in the URN and a bare slug everywhere
  // the page and its API look it up.
  routine: (id) =>
    `/settings/routines/${id.startsWith(SYSTEM_ROUTINE_URN_PREFIX) ? id.slice(SYSTEM_ROUTINE_URN_PREFIX.length) : id}`,
};

/** Schemes that address something outside this app and are safe to keep verbatim. */
const EXTERNAL_SCHEMES = ["http://", "https://", "mailto:", "tel:"];

/**
 * A bare hostname the author wrote without a scheme (`calendly.com/x`). Requires
 * a dot inside the first segment so a rootless path (`threads/7`) and a bare word
 * are not silently turned into someone else's website.
 */
const BARE_DOMAIN_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)+(?::\d+)?(?:[/?#]|$)/i;

/**
 * Resolve one markdown href.
 *
 * Order matters only in that the app's own schemes are claimed before the
 * external fallback; nothing here reads ambient state, so every surface gets the
 * same answer for the same string.
 */
export function resolveMessageLink(href: string): ResolvedMessageLink {
  const raw = href.trim();
  if (raw === "") return NONE;

  if (raw.startsWith("#")) {
    const hash = raw.slice(1);
    return hash === "" ? NONE : { kind: "anchor", hash };
  }

  if (raw.startsWith(ACTION_HREF_PREFIX)) {
    // An unknown action is a link to nothing rather than a link to a guess: the
    // id names a side effect this build has no implementation for.
    const action = raw.slice(ACTION_HREF_PREFIX.length);
    return isKnownAction(action) ? { kind: "action", action } : NONE;
  }

  if (raw.startsWith("urn:")) return resolveUrn(raw);

  // A protocol-relative `//host` is a real external link, not a rooted path, and
  // must be checked before the single-slash branch claims it.
  if (raw.startsWith("//")) return { kind: "external", url: raw };
  if (raw.startsWith("/")) return resolveAppPath(raw);

  for (const scheme of EXTERNAL_SCHEMES) {
    if (raw.toLowerCase().startsWith(scheme)) return { kind: "external", url: raw };
  }

  // Anything left is either a hostname the author didn't scheme, or junk. Only
  // the former earns a link — inventing `https://` for the rest is how a stray
  // `urn:bb:routine:9` used to render as a broken external URL.
  if (BARE_DOMAIN_RE.test(raw)) return { kind: "external", url: `https://${raw}` };

  return NONE;
}

/** Map a canonical entity URN to the page that serves it. */
function resolveUrn(raw: string): ResolvedMessageLink {
  const urn = safeParseURN(raw);
  if (!urn) return NONE;

  const route = URN_ROUTES[urn.type];
  if (route) {
    // Ids are checked in the form their type is addressed by. A routine is a
    // UUID or a `system:<slug>` sentinel; everything else is a numeric ent id,
    // and a URN carrying anything else names a row no loader can fetch.
    const valid = urn.type === "routine" ? isSegmentedUrn(raw) : isUrn(raw);
    return valid ? { kind: "app", path: route(urn.id) } : NONE;
  }

  // Every remaining canvas type is an artifact type, addressed by the
  // type-qualified path so the URN round-trips out of the URL.
  if (CANVAS_URN_TYPES.has(urn.type) && canvasUrnIsValid(raw, urn.type)) {
    return { kind: "app", path: `/artifacts/${urn.type}/${urn.id}` };
  }

  return NONE;
}

/** Validate a rooted path against the routes the app actually serves. */
function resolveAppPath(raw: string): ResolvedMessageLink {
  // Backslashes are not path separators, but special-scheme URL parsing
  // normalizes them as if they were — `/\evil.example` would change meaning
  // during absolutization. Reject rather than emit a target we did not intend.
  if (raw.includes("\\")) return NONE;

  const [path] = raw.split(/[?#]/);
  const [, segment = "", first = "", second = ""] = path.split("/");

  const shape = APP_ROUTES[segment];
  if (shape === undefined) return NONE;
  if (shape === "id" && first === "") return NONE;
  // `/calendar` alone is the calendar; `/calendar/event` without an id names no
  // event, and is the one deep form worth rejecting here rather than rendering
  // a link to a page that cannot load.
  if (segment === "calendar" && first === "event" && second === "") return NONE;

  return { kind: "app", path: raw };
}
